import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type Drueckteil, type ArbeitSession } from '../lib/api';
import { moduleApi, type ModuleItem } from '../lib/moduleApi';
import { StandCell } from '../components/StandCell';
import { QSMeldenModal } from '../components/QSMeldenModal';
import { EmpfohleneReihenfolge } from '../components/EmpfohleneReihenfolge';

type ModulePath = 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage';

interface RowItem {
  id: number;
  module_table: ModulePath;
  bestellnr: string;
  dv_nummer: string | null;
  stueck: number;
  gesamtzeit: number;
  bezeichnung: string | null;
  material: string | null;
  lieferdatum: string;
  kw: number;
  priority: 'normal' | 'eilig' | 'kritisch';
  fertig: number;
  stand_id: number | null;
  stand_wert: string | null;
  stand_farbe: string | null;
  kunde_name: string | null;
  updated_at: string;
}

const MODULE_LABELS: Record<ModulePath, string> = {
  drueckteile: 'Drückteile',
  laufraeder:  'Laufräder',
  baugruppen:  'Baugruppen',
  montage:     'Montage',
};

const LS_ABT_KEY = 'sickinger_werker_abteilung_id';

export function Werkstatt() {
  const users      = useApi(() => api.lookup.users(), []);
  const abteilungen = useApi(() => api.lookup.abteilungen(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);

  // Seçilen abteilung_id (localStorage'da saklanır)
  const [abteilungId, setAbteilungId] = useState<number | null>(() => {
    const v = Number(window.localStorage.getItem(LS_ABT_KEY) || '0');
    return v > 0 ? v : null;
  });

  // Abteilung için temsilci user ("[WS] Abteilung_Name")
  const werkerId = useMemo(() => {
    if (!abteilungId || !users.data) return null;
    const userList = users.data.data;
    const abtUser = userList.find(u => u.abteilung_id === abteilungId && u.name?.startsWith('[WS] '));
    return abtUser?.id ?? null;
  }, [abteilungId, users.data]);

  const currentAbteilung = abteilungen.data?.data.find(a => a.id === abteilungId);
  const [search, setSearch]       = useState('');
  const [modulFilter, setModulFilter] = useState<ModulePath | 'alle'>('alle');
  const [toast, setToast]         = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [qsModal, setQsModal] = useState<RowItem | null>(null);
  const [visibleCount, setVisibleCount] = useState(30);
  // Reset Sichtbarkeit bei Filter-/Suchänderung
  useEffect(() => { setVisibleCount(30); }, [search, modulFilter, abteilungId]);

  // Liste:
  //  - Wenn Abteilung gewählt → nutze /api/sessions/abteilung/:id/auftraege
  //    (Server weiß: Hauptmodule = alle offen, Seitenabteilung = stand-gefiltert)
  //  - Ohne Abteilung → wie bisher alle 4 Module (modul-Filter greift)
  const list = useApi(async () => {
    if (abteilungId) {
      const res = await api.sessions.abteilungAuftraege(abteilungId);
      const items: RowItem[] = res.data.map((r) => ({
        id: r.id,
        module_table: r.modul as ModulePath,
        bestellnr: r.bestellnr,
        dv_nummer: r.dv_nummer,
        stueck: r.stueck,
        gesamtzeit: r.gesamtzeit,
        bezeichnung: r.bezeichnung,
        material: null,
        lieferdatum: r.lieferdatum,
        kw: r.kw,
        priority: r.priority as RowItem['priority'],
        fertig: r.fertig,
        stand_id: r.stand_id,
        stand_wert: r.stand_wert,
        stand_farbe: r.stand_farbe,
        kunde_name: r.kunde_name,
        updated_at: r.updated_at,
      }));
      // Client-seitige Suche + Modul-Filter (abteilungAuftraege hat keine Query-Params)
      const q = search.toLowerCase().trim();
      return items
        .filter((r) => modulFilter === 'alle' ? true : r.module_table === modulFilter)
        .filter((r) => !q ||
          r.bestellnr.toLowerCase().includes(q) ||
          (r.dv_nummer ?? '').toLowerCase().includes(q) ||
          (r.kunde_name ?? '').toLowerCase().includes(q) ||
          (r.bezeichnung ?? '').toLowerCase().includes(q))
        .sort((a, b) => a.lieferdatum.localeCompare(b.lieferdatum));
    }

    // Fallback: keine Abteilung — alle Module
    const targets: ModulePath[] = modulFilter === 'alle'
      ? ['drueckteile','laufraeder','baugruppen','montage']
      : [modulFilter];
    const results = await Promise.all(targets.map(async (m) => {
      if (m === 'drueckteile') {
        const res = await api.drueckteile.list({ q: search || undefined, fertig: 0, limit: 40, sort: 'lieferdatum', dir: 'asc' });
        return res.data.map<RowItem>(r => ({
          id: r.id, module_table: 'drueckteile',
          bestellnr: r.bestellnr, dv_nummer: r.dv_nummer, stueck: r.stueck,
          gesamtzeit: r.gesamtzeit,
          bezeichnung: (r as unknown as { masse_bezeichnung?: string | null }).masse_bezeichnung ?? null,
          material: r.material ?? null,
          lieferdatum: r.lieferdatum, kw: r.kw, priority: r.priority,
          fertig: r.fertig, stand_id: r.stand_id,
          stand_wert: r.stand_wert ?? null, stand_farbe: r.stand_farbe ?? null,
          kunde_name: r.kunde_name ?? null, updated_at: r.updated_at,
        }));
      }
      const res = await moduleApi(m).list({ q: search || undefined, fertig: 0, limit: 40, sort: 'lieferdatum', dir: 'asc' });
      return res.data.map<RowItem>((r: ModuleItem) => ({
        id: r.id, module_table: m,
        bestellnr: r.bestellnr, dv_nummer: r.dv_nummer, stueck: r.stueck,
        gesamtzeit: r.gesamtzeit,
        bezeichnung: r.bezeichnung, material: null,
        lieferdatum: r.lieferdatum, kw: r.kw, priority: r.priority,
        fertig: r.fertig, stand_id: r.stand_id,
        stand_wert: r.stand_wert, stand_farbe: r.stand_farbe,
        kunde_name: r.kunde_name ?? null, updated_at: r.updated_at,
      }));
    }));
    return results.flat().sort((a, b) => a.lieferdatum.localeCompare(b.lieferdatum));
  }, [search, modulFilter, abteilungId]);

  // Aktif session'ları yükle — hangi kayıt üstünde kim çalışıyor
  const active = useApi(() => api.sessions.active(), [werkerId]);

  // Her 30 sn'de aktif session'ları ve liste'yi yenile (vorher 5 sn — zu aggressiv bei 250+ Aufträgen)
  useEffect(() => {
    const doReload = () => { active.reload(); list.reload(); };
    const t = setInterval(doReload, 30_000);
    const onVis = () => { if (document.visibilityState === 'visible') doReload(); };
    const onFocus = () => doReload();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abteilungId, search, modulFilter]);

  const activeByRecord = useMemo(() => {
    const map = new Map<string, ArbeitSession>();
    (active.data?.data ?? []).forEach(s => {
      map.set(`${s.module_table}:${s.record_id}`, s);
    });
    return map;
  }, [active.data]);

  const mySession = useMemo(() =>
    (active.data?.data ?? []).find(s => s.user_id === werkerId) ?? null,
    [active.data, werkerId]
  );

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const selectAbteilung = (id: number | null) => {
    setAbteilungId(id);
    if (id) window.localStorage.setItem(LS_ABT_KEY, String(id));
    else    window.localStorage.removeItem(LS_ABT_KEY);
  };

  const startWork = async (row: RowItem) => {
    if (!werkerId || !abteilungId) { flash('Bitte zuerst oben Abteilung wählen'); return; }

    // Pre-Start-Check: Beistelteile-Status für diesen Auftrag
    try {
      const bst = await api.beistelteile.listForAuftrag(row.module_table, row.id);
      const fehlende = bst.data.filter(b => b.status === 'erwartet' || b.status === 'fehlt');
      if (fehlende.length > 0) {
        const liste = fehlende.map(b => {
          const overdue = b.erwartet_bis && b.erwartet_bis < new Date().toISOString().slice(0,10);
          return `• ${b.bezeichnung} (${b.erwartete_stueckzahl} Stk)${overdue ? ' — ÜBERFÄLLIG' : ''}`;
        }).join('\n');
        const ok = window.confirm(
          `⚠ ${fehlende.length} Beistelteil${fehlende.length>1?'e':''} noch nicht eingegangen:\n\n` +
          liste + '\n\nTrotzdem starten?'
        );
        if (!ok) return;
      }
    } catch {
      // Beistelteile-Check schlägt fehl → Start trotzdem erlauben (keine harte Abhängigkeit)
    }

    setUpdatingId(`${row.module_table}:${row.id}`);
    try {
      await api.sessions.start({
        module_table: row.module_table,
        record_id: row.id,
        user_id: werkerId,
        abteilung_id: abteilungId,
      });
      flash(`▶ Gestartet: ${row.bestellnr}`);
      active.reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setUpdatingId(null);
    }
  };

  const endWork = async (session: ArbeitSession) => {
    setUpdatingId(`end:${session.id}`);
    try {
      await api.sessions.end(session.id);
      flash(`⏹ Beendet`);
      active.reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setUpdatingId(null);
    }
  };

  const markFertig = async (row: RowItem) => {
    if (!confirm(`"${row.bestellnr}" als FERTIG markieren?`)) return;
    setUpdatingId(`fertig:${row.module_table}:${row.id}`);
    try {
      const body = { fertig: 1, updated_at: row.updated_at, user_id: werkerId ?? undefined };
      if (row.module_table === 'drueckteile') {
        await api.drueckteile.update(row.id, body as unknown as Partial<Drueckteil>);
      } else {
        await moduleApi(row.module_table).update(row.id, body as Partial<ModuleItem>);
      }
      // Eğer bu kayıtta aktif session varsa bitir
      const sess = activeByRecord.get(`${row.module_table}:${row.id}`);
      if (sess) await api.sessions.end(sess.id);
      flash(`✓ ${row.bestellnr} ist fertig`);
      list.reload();
      active.reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setUpdatingId(null);
    }
  };

  // "Versandbereit" = stand'ı Versandbereit yap (fertig=0 kalır). İş Versand abteilungunda görünür.
  const markVersandbereit = async (row: RowItem) => {
    const versandbereitStand = standWerte.data?.data.find(s => s.wert === 'Versandbereit');
    if (!versandbereitStand) { flash('Stand "Versandbereit" nicht gefunden'); return; }
    if (!confirm(`"${row.bestellnr}" als VERSANDBEREIT markieren?\n→ wandert in die Versand-Abteilung`)) return;
    setUpdatingId(`versand:${row.module_table}:${row.id}`);
    try {
      const body = { stand_id: versandbereitStand.id, updated_at: row.updated_at, user_id: werkerId ?? undefined };
      if (row.module_table === 'drueckteile') {
        await api.drueckteile.update(row.id, body as Partial<Drueckteil>);
      } else {
        await moduleApi(row.module_table).update(row.id, body as Partial<ModuleItem>);
      }
      // Aktif session varsa kapat (bu abteilungun işi bitti)
      const sess = activeByRecord.get(`${row.module_table}:${row.id}`);
      if (sess) await api.sessions.end(sess.id);
      flash(`📦 ${row.bestellnr} → Versand`);
      list.reload();
      active.reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto relative">
      {toast && (
        <div className="fixed top-4 right-4 bg-emerald-600 text-white rounded-lg px-4 py-2 shadow-lg text-sm z-50">
          {toast}
        </div>
      )}

      {/* HEADER — Abteilung-Wahl + Modul Filter */}
      <div className="text-white rounded-xl p-4 mb-4"
           style={{ background: currentAbteilung ? darken(currentAbteilung.farbe) : '#0C4A6E' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase opacity-80">Werkstatt-Modus</div>
            <h1 className="text-2xl font-bold mt-1">
              {currentAbteilung ? currentAbteilung.name : 'Abteilung wählen'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm opacity-80">Abteilung:</span>
            <select
              value={abteilungId ?? ''}
              onChange={e => selectAbteilung(e.target.value ? Number(e.target.value) : null)}
              className="text-base text-slate-900 rounded-lg px-3 py-2 min-w-[220px] font-semibold">
              <option value="">— Abteilung wählen —</option>
              {abteilungen.data?.data.map(a =>
                <option key={a.id} value={a.id}>{a.name}</option>
              )}
            </select>
          </div>
        </div>

        {mySession && (
          <div className="mt-3 bg-sky-800/50 rounded-lg p-3 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase text-sky-200">Aktuelle Arbeit</div>
              <div className="font-semibold">
                {MODULE_LABELS[mySession.module_table]} · {mySession.auftrag?.bestellnr ?? '#' + mySession.record_id}
                {' — '}
                <span className="text-sky-200">seit {Math.floor((mySession.running_minutes ?? 0))} Min</span>
              </div>
            </div>
            <button onClick={() => endWork(mySession)}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-5 py-2 font-bold">
              ⏹ Stop
            </button>
          </div>
        )}
      </div>

      {/* Modül tab + Scan */}
      <div className="bg-white rounded-xl p-3 border border-slate-200 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {(['alle','drueckteile','laufraeder','baugruppen','montage'] as const).map(m => (
            <button key={m} onClick={() => setModulFilter(m)}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      modulFilter === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}>
              {m === 'alle' ? 'Alle' : MODULE_LABELS[m as ModulePath]}
            </button>
          ))}
        </div>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="🔍 DV-Nr oder Bestellnr …"
               className="flex-1 text-lg px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30" />
        <div className="text-sm text-slate-500 px-2">
          {list.data?.length ?? 0} offen
          {list.data && list.data.length > visibleCount && (
            <span className="ml-1 text-slate-400">· zeige {visibleCount}</span>
          )}
        </div>
      </div>

      {/* A3 — Empfohlene Reihenfolge (nur wenn Abteilung gewählt) */}
      {abteilungId && currentAbteilung && (
        <div className="mb-4">
          <EmpfohleneReihenfolge
            abteilungId={abteilungId}
            abteilungName={currentAbteilung.name}
            limit={5}
          />
        </div>
      )}

      {/* Offene Aufträge */}
      <div className="space-y-3">
        {list.loading && <div className="text-center py-8 text-slate-500">Lädt …</div>}        {list.data && list.data.length === 0 && (
          <div className="text-center py-12 px-6 text-slate-500 bg-white rounded-xl border border-dashed border-slate-300">
            <div className="text-lg font-medium mb-2">Keine offenen Aufträge</div>
            {abteilungId && currentAbteilung && (
              <div className="text-sm max-w-md mx-auto">
                {`Aktive Abteilung: ${currentAbteilung.name}. Starte einen offenen Auftrag, um ihn dieser Abteilung zuzuordnen.`}
              </div>
            )}
          </div>
        )}
        {list.data?.slice(0, visibleCount).map(d => {
          const key = `${d.module_table}:${d.id}`;
          const sess = activeByRecord.get(key);
          const isMine = sess && sess.user_id === werkerId;
          const isBusy = !!sess && !isMine;
          const updKey = updatingId;

          return (
            <div key={key} className={`bg-white rounded-xl p-4 ${
              d.priority === 'kritisch' ? 'border-4 border-red-400' :
              d.priority === 'eilig'    ? 'border-2 border-red-300' :
              isMine                    ? 'border-2 border-sky-500' :
              'border border-slate-200'
            } ${updKey === key ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                      {MODULE_LABELS[d.module_table]}
                    </span>
                    {d.priority === 'eilig' &&
                      <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">⚠ EILIG</span>}
                    {d.priority === 'kritisch' &&
                      <span className="bg-red-800 text-white text-xs font-bold px-2 py-0.5 rounded-full">⚠ KRITISCH</span>}
                    <Link to={`/${d.module_table}/${d.id}`} className="font-mono text-xs text-slate-500 hover:text-brand hover:underline">
                      DV {d.dv_nummer ?? '—'}
                    </Link>
                    {sess && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        isMine ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {isMine ? `▶ ICH — ${Math.floor(sess.running_minutes ?? 0)}min`
                                : `⚠ ${sess.user_name} seit ${Math.floor(sess.running_minutes ?? 0)}min`}
                      </span>
                    )}
                  </div>
                  <div className="text-xl font-bold text-slate-900">{d.kunde_name ?? '—'}</div>
                  <div className="text-base text-slate-700 truncate">{d.bezeichnung ?? '—'}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    {d.bestellnr} · {d.stueck} Stk · {d.gesamtzeit}h
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500">Liefertermin</div>
                  <div className="text-lg font-bold">{formatDateShort(d.lieferdatum)}</div>
                  <div className="text-xs text-slate-500">KW {d.kw}</div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100 flex-wrap">
                <div className="flex items-center gap-3 flex-1">
                  <div className="text-xs text-slate-500 uppercase">Stand:</div>
                  <StandCell
                    drueckteilId={d.id}
                    currentStandId={d.stand_id}
                    currentStandWert={d.stand_wert}
                    currentStandFarbe={d.stand_farbe}
                    updatedAt={d.updated_at}
                    standWerte={standWerte.data?.data ?? []}
                    onChanged={() => list.reload()}
                    modulePath={d.module_table}
                    actingUserId={werkerId} />
                </div>

                <div className="flex gap-2 items-center">
                  {!sess && (
                    <button onClick={() => startWork(d)}
                            disabled={!werkerId || updKey === key}
                            title={!werkerId ? 'Erst oben Abteilung wählen' : 'Start'}
                            className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-5 py-3 font-bold disabled:opacity-40 disabled:cursor-not-allowed">
                      ▶ Start
                    </button>
                  )}
                  {isMine && (
                    <button onClick={() => endWork(sess!)} disabled={updKey === `end:${sess!.id}`}
                            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-5 py-3 font-bold disabled:opacity-50">
                      ⏹ Stop
                    </button>
                  )}
                  {isBusy && (
                    <div className="text-xs text-amber-700 font-medium px-2">
                      ▶ {(sess.user_name ?? '').replace(/^\[WS\]\s*/, '')}<br/>
                      <span className="text-slate-500">{sess.running_minutes ?? 0}min</span>
                    </div>
                  )}
                  <button onClick={() => setQsModal(d)}
                          title="Problem an QS melden"
                          className="bg-red-100 hover:bg-red-200 text-red-700 border-2 border-red-300 rounded-xl px-3 py-3 font-bold">
                    ⚠
                  </button>
                  <button onClick={() => markVersandbereit(d)}
                          disabled={updKey?.startsWith('versand:')}
                          title="Als versandbereit markieren (wandert in Versand-Abteilung)"
                          className="bg-sky-100 hover:bg-sky-200 text-sky-800 border-2 border-sky-300 rounded-xl px-4 py-3 font-bold disabled:opacity-50">
                    📦 Versand
                  </button>
                  <button onClick={() => markFertig(d)} disabled={updKey?.startsWith('fertig:')}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl px-5 py-3 font-bold">
                    ✓ FERTIG
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {list.data && list.data.length > visibleCount && (
          <div className="text-center py-4">
            <button
              onClick={() => setVisibleCount(c => c + 30)}
              className="px-5 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              + {Math.min(30, list.data.length - visibleCount)} weitere zeigen
              <span className="text-slate-400 ml-2">({list.data.length - visibleCount} verbleibend)</span>
            </button>
          </div>
        )}
      </div>

      {qsModal && (
        <QSMeldenModal
          modul={qsModal.module_table}
          recordId={qsModal.id}
          bestellnr={qsModal.bestellnr}
          kundeName={qsModal.kunde_name}
          userId={werkerId}
          abteilungId={abteilungId}
          onClose={() => setQsModal(null)}
          onSaved={() => flash('⚠ An QS gemeldet')} />
      )}
    </div>
  );
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function darken(hex: string): string {
  if (!hex || !hex.startsWith('#')) return '#0C4A6E';
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xFF) * 0.55));
  const g = Math.max(0, Math.round(((n >> 8)  & 0xFF) * 0.55));
  const b = Math.max(0, Math.round((n & 0xFF) * 0.55));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
